import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button, IconButton, Select, TextField } from '@radix-ui/themes'
import { LocateFixed, Search } from 'lucide-react'

export const DEFAULT_DISTANCE = "10000"

type Props = {
  /** Inertia path the search submits back to. Query params already on it (`?view=map`) are kept. */
  path: string
  initialSearch: string
  initialDistance: string
  /** Called as a search or locate goes out — the map's mobile tabs flip back to the list. */
  onSearch?: () => void
  /** `stacked` for the map's narrow rail; `inline` puts it on one row from `sm` up. */
  layout?: 'stacked' | 'inline'
}

/**
 * Place search + "use my location" + distance, submitting `search` and
 * `distance` back to the page. Shared by the map rail and the events feed.
 */
export default function LocationSearch({ path, initialSearch, initialDistance, onSearch, layout = 'stacked' }: Props) {
  const [search, setSearch] = useState(initialSearch || "")
  const [distance, setDistance] = useState(initialDistance || DEFAULT_DISTANCE)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    onSearch?.()
    router.get(path, { search, distance })
  }

  const handleLocate = () => {
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      onSearch?.()
      router.get(path, { search: `${coords.latitude}, ${coords.longitude}`, distance })
    })
  }

  return (
    <form onSubmit={handleSearch} className={`flex flex-col gap-3 ${layout === 'inline' ? 'sm:flex-row sm:items-center' : ''}`}>
      <div className={layout === 'inline' ? 'sm:flex-1' : ''}>
        <TextField.Root
          size="3"
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by city or address"
          aria-label="Location"
          autoComplete="off"
        >
          <TextField.Slot>
            <IconButton type="button" variant="ghost" color="gray" onClick={handleLocate} aria-label="Use my location">
              <LocateFixed className="h-5 w-5" />
            </IconButton>
          </TextField.Slot>
        </TextField.Root>
      </div>
      <div className="flex items-center justify-between gap-3">
        <Select.Root size="3" value={distance} onValueChange={setDistance}>
          <Select.Trigger aria-label="Distance" />
          <Select.Content>
            <Select.Item value="25">25 Miles</Select.Item>
            <Select.Item value="50">50 Miles</Select.Item>
            <Select.Item value="100">100 Miles</Select.Item>
            <Select.Item value="250">250 Miles</Select.Item>
            <Select.Item value="500">500 Miles</Select.Item>
            <Select.Item value="10000">All</Select.Item>
          </Select.Content>
        </Select.Root>
        <Button type="submit" size="3">
          <Search className="h-5 w-5" />
          Search
        </Button>
      </div>
    </form>
  )
}
